<template>
  <div class="gmf-active-members-grid-wrap">
    <div
      v-for="slot in slots"
      :key="slot.idx"
      class="gmf-active-member-slot"
      :class="{
        'gmf-active-member-grid-poppedout': slot.disabled,
      }"
    >
      <Avatar
        v-if="slot.member"
        :class="{
          'gmf-avatar-faded': ! slot.member.isOnline && enableFadedAvatars,
          'gmf-avatar-pop': slot.pop,
        }"
        :customer="slot.member"
        :disableObjectHack="true"
      />
      <div class="gmf-member-activity-bubble" v-if="slot.member && slot.member.comment" :class="{'gmf-member-activity-bubble-hidden': ! slot.showActivity}">
        <img :src="slot.member.comment.content.images.length > 0 ? slot.member.comment.content.images[0].thumbnailUrl : slot.member.comment.target.imageUrl" />
        <Icon v-if="slot.member.comment.type.name === 'product_review'" style="--gmf-icon-color: var(--gmf-col-product_score);" icon="product_score" />
      </div>
    </div>
    <div ref="placeholder" class="gmf-active-member-slot gmf-hidden"></div>
  </div>
</template>

<style>
.gmf-active-members-grid {
  width: 100%;
  height: 241px;
}

.gmf-active-members-grid-wrap {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  align-content: center;
  height: 100%;
  gap: 22px;
  margin: auto;
  width: 100%;
  position: relative;
  flex-direction: row-reverse;
}

.gmf-active-members-grid .gmf-active-member-slot {
  width: 80px;
  height: 80px;
  position: relative;
  transform: scale(1);
  transform-origin: center;
  transition: all 0.3s ease-out;
  will-change: transform;
}
.gmf-active-members-grid .gmf-avatar {
  width: 100%;
  height: 100%;
  background-position: center;
  background-size: 100%;
  transform: scale(1);
  transform-origin: center;
  transition: all 0.3s ease-out;
  cursor: pointer;
  will-change: transform;
}
.gmf-active-members-grid .gmf-avatar-faded {
  opacity: 0.5;
  filter: grayscale(0.7);
  transition: all 0.3s ease-out;
}
@media (max-width: 480px) {
  .gmf-active-members-grid-wrap {
    gap: 20px;
  }
  .gmf-active-members-grid .gmf-active-member-slot {
    width: 64px;
    height: 64px;
  }
}


.gmf-active-members-grid .gmf-avatar-pop {
  animation: 0.5s gmf-active-member-pop ease-in-out forwards;
}
@keyframes gmf-active-member-pop {
  50% {
    transform: scale(1.1);
  }
  100% {
    transform: scale(1);
  }
}

.gmf-active-members-grid .gmf-active-member-grid-poppedout {
  transform: scale(0.6) !important;
  opacity: 0 !important;
}

@media (hover) {
  .gmf-active-members-grid .gmf-avatar:hover {
    transform: scale(1.2) !important;
    box-shadow: 0px 2px 5px rgba(0,0,0,.2);
  }
  .gmf-active-members-grid .gmf-avatar:hover {
    opacity: 1;
    filter: grayscale(0);
  }
}

.gmf-member-activity-bubble {
  padding: var(--gmf-p-1);
  position: absolute;
  width: 40px;
  height: 32px;
  right: -20px;
  top: -16px;
  background-size: contain;
  background-position: center;
  background-repeat: no-repeat;
  background: var(--gmf-col-white);
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 5px;
  filter: drop-shadow(0px 2px 4px rgba(0,0,0,.2));

  transform-origin: center;
  transition: all 0.3s ease-out;
  will-change: transform;

  pointer-events: none;
}
.gmf-member-activity-bubble-hidden {
  transform: scale(0.5) translate(0, 50px);
  opacity: 0;
}

/* @TODO only for review type */
.gmf-member-activity-bubble .gmf-icon {
  position: absolute;
  width: 20px;
  height: 20px;
  top: -8px;
  left: -5px;
  z-index: 1;
  filter: drop-shadow(1px 1px 0 white) drop-shadow(-1px -1px 0 white) drop-shadow(1px -1px 0 white) drop-shadow(-1px 1px 0 white);
}
.gmf-member-activity-bubble::before {
  content: "";
  background: var(--gmf-col-white);
  display: block;
  position: absolute;
  width: 18px;
  height: 16px;
  bottom: -6px;
  left: 8px;
  transform: skewX(-51deg) rotate(45deg);
  border-radius: 3px;
}
.gmf-member-activity-bubble img {
  max-height: 100%;
  max-width: 100%;
  z-index: 1;
  border-radius: 3px;
}
</style>

<script>
import state from '@/state';
import callApi from '@/utils/callApi';
import Icon from '@/components/Icon';
import throttle from '@/utils/throttle';
import Avatar from '@/components/Avatar';
import elSize from '@/utils/mixins/elSize';

export default {
  name: 'ActiveMembersGrid',
  mixins: [elSize],
  props: ['manualStart', 'inViewport'],
  components: { Avatar, Icon },
  data () {
    return {
      cols: 14,
      rows: 3,
      members: [],
      slots: [],
      state,
      hasStarted: false,
      enableFadedAvatars: false,
    };
  },
  computed: {
    itemCount () {
      return this.cols * this.rows;
    },
    popupProfileCardEnabled () {
      return !!this.state.merchantConfig?.popupProfileCard?.enabled;
    },
    freeSlots () {
      return this.slots.filter(s => s.member === null);
    },
    unassignedMembers () {
      return this.members.filter(m => m.slot === null);
    },
    onScroll () {
      return throttle(this.startIfInViewport, 100);
    },
    translationsEnabled() {
      return this.state.merchantConfig?.widgets?.translations.mode !== 'translateNothing';
    },
  },
  methods: {
    fillSlots () {
      // @TODO if members already have slots assigned, this will break
      this.slots = [];
      for (let i = 0; i < this.itemCount; ++i) {
        this.slots.push({
          idx: i,
          member: null,
          disabled: true,
          pop: false,
          showActivity: false,
        });
      }
    },
    calculateSize () {
      const gap = parseInt(window.getComputedStyle(this.$el).gap);
      const avatarSize = parseInt(window.getComputedStyle(this.$refs.placeholder).width);

      let cols = 1 + Math.floor((this.elSize.width - avatarSize) / (avatarSize + gap));
      let rows = 1 + Math.floor((this.elSize.height - avatarSize) / (avatarSize + gap));

      if (rows > 8) {
        rows = 8;
      }

      this.cols = cols;
      this.rows = rows;
    },
    async startIfInViewport () {
      const r = this.$el.getBoundingClientRect();
      if (r.top <= (window.innerHeight || document.documentElement.clientHeight)
        && r.top + r.height > 0) {
        await this.start();
      }
    },
    async loadMembers () {
      if (this.members && this.members.length > 0) {
        return;
      }

      const commentPromise = callApi('GET', this.state.commentSearchEndpoint + `?type=product_review&type=social_post&no_count=1&reply_depth=0&take=8&is_member=1&order=created.desc&translate=${this.translationsEnabled}`);
      const res = await callApi('GET', '/v1/client/active-members');
      const { members } = await res.json();

      this.members = members;

      const commentRes = await commentPromise;
      const { comments } = await commentRes.json();

      const memberMap = {};
      for (const member of members) {
        memberMap[member.id] = member;
      }

      for (const comment of comments.reverse()) {
        if (comment.type.name !== 'product_review' && comment.content.images.length === 0) {
          continue;
        }
        if (comment.author.id in memberMap) {
          memberMap[comment.author.id].comment = comment;
        } else {
          const member = {
            id: comment.author.id,
            avatarCode: comment.author.avatarCode,
            avatarUrl: comment.author.avatarUrl,
            isOnline: comment.author.isOnline,
            lastOnline: comment.author.lastOnline,
            comment,
          };
          memberMap[member.id] = member;
          this.members.unshift(member);
        }
      }
    },
    async popAvatars () {
      let i = 0;
      for (let y = 0; y < this.rows; ++y) {
        for (let x = 0; x < this.cols; ++x) {
          const slot = this.slots[i++];

          if (slot.member && slot.member.isOnline) {
            setTimeout(() => {
              slot.pop = true;
              setTimeout(()=>{
                slot.pop = false;
              }, 700);
            }, x * 50 + y * 25);
          }
        }
      }
    },
    /*
    Start playing animations
    */
    async start () {
      this.removeHandlers();

      if (! this.hasStarted) {
        this.hasStarted = true;
        await this.loadMembers();
      }

      const freeSlots = this.freeSlots;

      if (this.popInterval) {
        clearInterval(this.popInterval);
      }
      /*
      @TODO vi testar att ta bort avatarpops, förhoppningsvis ger detta bättre prestanda på safari
      this.popInterval = setInterval(this.popAvatars, 4000);
      */

      for (const member of this.members) {
        if (freeSlots.length > 0) {
          const slotIdx = ~~(Math.random() * freeSlots.length);
          const slot = freeSlots[slotIdx];
          freeSlots.splice(slotIdx, 1);
          member.slot = slot;
          slot.member = member;

          let timeToAppear = 50 + ~~(Math.random() * 2000);
          let timeToDisappear = null;

          if (member.isOnline && Math.random() > 0.65) {
            timeToAppear = 1000 + ~~(Math.random() * 5000);
          } else if (! member.isOnline && Math.random() > 0.8 && ! member.comment) {
             timeToAppear = 50 + ~~(Math.random() * 1500);
             timeToDisappear = 500 + ~~(Math.random() * 8000);
          }

          setTimeout(() => {
            slot.disabled = false;
            if (timeToDisappear) {
              setTimeout(() => {
                this.clearSlot(slot);
              }, timeToDisappear);
            }
            if (member.comment) {
              setTimeout(() => {
                slot.showActivity = true;
              }, 500 + Math.random() * 4000);
            }
          }, timeToAppear);
        } else {
          member.slot = null;
        }
      }
    },
    clearSlot(slot) {
      slot.disabled = true;
      setTimeout(() => {
        if (slot.member && slot.member.slot === slot) {
          slot.member.slot = null;
          slot.member = null;
        }
      }, 500);
    },
    removeHandlers() {
      document.removeEventListener('scroll', this.onScroll);
      document.body.removeEventListener('touchmove', this.onScroll);
      if (this.popInterval) {
        clearInterval(this.popInterval);
      }
    },
    getRandomFreeSlot () {
      const freeSlots = this.freeSlots;
      return freeSlots[~~(Math.random() * freeSlots.length)];
    },
    getRandomUnassignedMember () {
      const unassignedMembers = this.unassignedMembers;
      return unassignedMembers[~~(Math.random() * unassignedMembers.length)];
    },
  },
  watch: {
    elSize () {
      this.calculateSize();
    },
    itemCount () {
      this.slots = []; // Needs to be empty for one frame or we will get wonky element growth
      this.$nextTick(()=>setTimeout(()=>{
        this.fillSlots();
        if (this.hasStarted) {
          this.start();
        } else {
          this.startIfInViewport();
        }
      }, 0));
    },
    inViewport (v) {
      if (v) {
        this.fillSlots();
        this.start();
      }
    },
  },
  async mounted () {
    this.fillSlots();

    if (this.manualStart) {
      if (this.inViewport) {
        this.start();
      }
    } else {
      await this.startIfInViewport();

      if (! this.hasStarted) {
        document.addEventListener('scroll', this.onScroll, { passive: true });
        document.body.addEventListener('touchmove', this.onScroll, { passive: true });
      }
    }
  },
  beforeUnmount () {
    this.removeHandlers();
  },
};
</script>
